import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { WarehouseService } from '../warehouse/warehouseService';
import { WarehouseInventoryService } from '../product/WarehouseInventoryService';

export interface DashboardData {
  // Product metrics
  totalProducts: number;
  activeProducts: number;
  lowStockProducts: number;
  outOfStockProducts: number;
  totalStockQuantity: number;
  productsWithVariants: number;
  featuredProducts: number;

  // Category metrics
  totalCategories: number;
  activeCategories: number;

  // Brand metrics
  totalBrands: number;
  activeBrands: number;

  // Warehouse metrics
  totalWarehouses: number;
  activeWarehouses: number;
  warehouses: WarehouseInfo[];

  // Order metrics
  totalOrders: number;
  pendingOrders: number;
  confirmedOrders: number;
  completedOrders: number;
  cancelledOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  recentOrders: OrderInfo[];

  // Revenue by status
  revenueByStatus: RevenueByStatus;

  // Product category distribution
  productsByCategory: CategoryDistribution[];

  // Stock alerts
  stockAlerts: StockAlert[];

  // Order trends (last 7 days)
  orderTrends: OrderTrend[];

  // Top products
  topProducts: TopProduct[];

  // Warehouse utilization
  warehouseUtilization: WarehouseUtilization[];
}

export interface WarehouseInfo {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  total: number;
  used: number;
  available: number;
  productCount: number;
  totalStock: number;
  utilizationPercentage: number;
}

export interface OrderInfo {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  itemCount: number;
  totalAmount: number;
  status: string;
  paymentStatus: string;
  orderDate: Date;
}

export interface RevenueByStatus {
  completed: number;
  pending: number;
  confirmed: number;
  cancelled: number;
  delivered: number;
}

export interface CategoryDistribution {
  categoryId: string;
  categoryName: string;
  productCount: number;
  percentage: number;
}

export interface StockAlert {
  productId: string;
  productName: string;
  sku: string;
  stockQuantity: number;
  minStockLevel: number;
  alertType: 'low' | 'out';
  warehouseCount: number;
  variantId?: string;
  variantName?: string;
}

export interface OrderTrend {
  date: string;
  orders: number;
  revenue: number;
}

export interface TopProduct {
  productId: string;
  productName: string;
  totalSold: number;
  revenue: number;
  imageUrl?: string;
}

export interface WarehouseUtilization {
  warehouseId: string;
  warehouseName: string;
  utilizationPercentage: number;
  used: number;
  total: number;
}

interface InventoryItem {
  productId: string;
  variantId?: string;
  warehouseId: string;
  quantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
  sku: string;
  productName: string;
  variantName?: string;
}

interface ProductInventorySummary {
  productId: string;
  productName: string;
  hasVariants: boolean;
  totalStock: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  variants?: {
    [variantId: string]: {
      variantName: string;
      sku: string;
      totalStock: number;
      isLowStock: boolean;
      isOutOfStock: boolean;
      minStockLevel: number;
      hasInventory: boolean; // Track if variant has any inventory records
    }
  };
}

export class DashboardService {
  /**
   * Fetches all dashboard data with optimized batch queries
   */
  static async getDashboardData(): Promise<DashboardData> {
    try {
      // Fetch all data in parallel with optimized queries
      const [
        productsSnapshot,
        categoriesSnapshot,
        brandsSnapshot,
        ordersSnapshot,
        warehousesSnapshot,
      ] = await Promise.all([
        getDocs(collection(db, 'products')),
        getDocs(collection(db, 'categories')),
        getDocs(collection(db, 'brands')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'warehouses')),
      ]);

      // Get all warehouse inventory data
      const warehouses = await WarehouseService.getActiveWarehouses();
      const allInventory = await this.getAllWarehouseInventory(warehouses);
      
      // Build product inventory summary from warehouse inventory
      const productInventorySummary = await this.buildProductInventorySummary(
        productsSnapshot,
        allInventory
      );

      // Process all data with the inventory summary
      const productsData = this.processProductsData(productsSnapshot, productInventorySummary);
      const categoriesData = this.processCategoriesData(categoriesSnapshot);
      const brandsData = this.processBrandsData(brandsSnapshot);
      const warehousesData = await this.processWarehousesData(warehouses);
      const ordersData = this.processOrdersData(ordersSnapshot);

      // Get additional analytics
      const productsByCategory = this.getProductsByCategoryDistribution(
        productsSnapshot,
        categoriesSnapshot
      );
      const stockAlerts = await this.getStockAlertsFromInventory(
        productsSnapshot,
        allInventory,
        productInventorySummary
      );
      const orderTrends = this.getOrderTrends(ordersSnapshot);
      const topProducts = this.getTopProducts(ordersSnapshot);

      return {
        ...productsData,
        ...categoriesData,
        ...brandsData,
        ...warehousesData,
        ...ordersData,
        productsByCategory,
        stockAlerts,
        orderTrends,
        topProducts,
        warehouseUtilization: warehousesData.warehouses.map(w => ({
          warehouseId: w.id,
          warehouseName: w.name,
          utilizationPercentage: w.utilizationPercentage,
          used: w.used,
          total: w.total,
        })),
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  /**
   * Get all warehouse inventory data
   */
  private static async getAllWarehouseInventory(warehouses: any[]): Promise<InventoryItem[]> {
    const allInventory: InventoryItem[] = [];
    
    for (const warehouse of warehouses) {
      try {
        const inventory = await WarehouseInventoryService.getWarehouseInventory(warehouse.id!);
        inventory.forEach(item => {
          allInventory.push({
            productId: item.productId,
            variantId: item.variantId || undefined,
            warehouseId: warehouse.id!,
            quantity: item.quantity || 0,
            minStockLevel: item.minStockLevel || 0,
            maxStockLevel: item.maxStockLevel || 0,
            isLowStock: item.isLowStock || false,
            isOutOfStock: item.isOutOfStock || false,
            isOverStock: item.isOverStock || false,
            sku: item.sku || '',
            productName: item.productName || 'Unknown',
            variantName: item.variantName || undefined,
          });
        });
      } catch (error) {
        console.error(`Error fetching inventory for warehouse ${warehouse.id}:`, error);
      }
    }
    
    return allInventory;
  }

  /**
   * Build product inventory summary from warehouse inventory
   */
  private static async buildProductInventorySummary(
    productsSnapshot: any,
    allInventory: InventoryItem[]
  ): Promise<Map<string, ProductInventorySummary>> {
    const productMap = new Map<string, ProductInventorySummary>();

    // First, create entries for all products
    productsSnapshot.forEach((doc: any) => {
      const product = doc.data();
      productMap.set(doc.id, {
        productId: doc.id,
        productName: product.name || 'Unknown',
        hasVariants: product.hasVariants || false,
        totalStock: 0,
        isLowStock: false,
        isOutOfStock: true, // Default to out of stock, will update if we find stock
        variants: {},
      });
    });

    // Process inventory to update stock levels
    allInventory.forEach((item: InventoryItem) => {
      const productId = item.productId;
      const productSummary = productMap.get(productId);
      
      if (!productSummary) return;

      if (!item.variantId) {
        // Simple product
        productSummary.totalStock += item.quantity;
        productSummary.isOutOfStock = false; // Product has some stock
        if (item.isLowStock) {
          productSummary.isLowStock = true;
        }
      } else {
        // Product with variants
        if (!productSummary.variants) {
          productSummary.variants = {};
        }
        
        const variantId = item.variantId;
        if (!productSummary.variants[variantId]) {
          productSummary.variants[variantId] = {
            variantName: item.variantName || 'Unknown Variant',
            sku: item.sku,
            totalStock: 0,
            isLowStock: false,
            isOutOfStock: true,
            minStockLevel: item.minStockLevel,
            hasInventory: false,
          };
        }
        
        const variant = productSummary.variants[variantId];
        variant.totalStock += item.quantity;
        variant.hasInventory = true; // Variant has inventory records
        
        // Variant is out of stock only if quantity is 0
        if (item.quantity > 0) {
          variant.isOutOfStock = false;
          productSummary.isOutOfStock = false; // At least one variant has stock
        }
        
        if (item.isLowStock) {
          variant.isLowStock = true;
        }
        
        // Update product total stock
        productSummary.totalStock += item.quantity;
      }
    });

    // After processing all inventory, determine product-level low stock status
    productMap.forEach((productSummary) => {
      if (productSummary.hasVariants && productSummary.variants) {
        // For products with variants, check if any variant is low stock
        const anyVariantLowStock = Object.values(productSummary.variants).some(
          variant => variant.isLowStock && !variant.isOutOfStock
        );
        productSummary.isLowStock = anyVariantLowStock;
      }
    });

    return productMap;
  }

  /**
   * Process products data using inventory summary
   */
  private static processProductsData(
    productsSnapshot: any,
    productInventorySummary: Map<string, ProductInventorySummary>
  ) {
    let totalProducts = 0;
    let activeProducts = 0;
    let lowStockProducts = 0;
    let outOfStockProducts = 0;
    let totalStockQuantity = 0;
    let productsWithVariants = 0;
    let featuredProducts = 0;

    productsSnapshot.forEach((doc: any) => {
      const product = doc.data();
      totalProducts++;

      if (product.hasVariants) {
        productsWithVariants++;
      }
      if (product.isFeatured) {
        featuredProducts++;
      }

      const productId = doc.id;
      const inventorySummary = productInventorySummary.get(productId);

      if (inventorySummary) {
        totalStockQuantity += inventorySummary.totalStock;

        // For products with variants, we need special logic
        if (product.hasVariants && inventorySummary.variants) {
          // A product with variants is considered out of stock only if ALL variants are out of stock
          const variants = Object.values(inventorySummary.variants);
          
          if (variants.length === 0) {
            // Product has variants but no inventory records at all
            outOfStockProducts++;
          } else {
            // Check if any variant has stock
            const anyVariantHasStock = variants.some(variant => !variant.isOutOfStock);
            
            if (anyVariantHasStock) {
              activeProducts++;
              // Check if any variant is low stock (but not out of stock)
              const anyVariantLowStock = variants.some(variant => 
                variant.isLowStock && !variant.isOutOfStock
              );
              if (anyVariantLowStock) {
                lowStockProducts++;
              }
            } else {
              // All variants are out of stock
              outOfStockProducts++;
            }
          }
        } else {
          // Simple product logic
          if (inventorySummary.isOutOfStock) {
            outOfStockProducts++;
          } else {
            activeProducts++;
            if (inventorySummary.isLowStock) {
              lowStockProducts++;
            }
          }
        }
      } else {
        // Product has no inventory records
        outOfStockProducts++;
      }
    });

    return {
      totalProducts,
      activeProducts,
      lowStockProducts,
      outOfStockProducts,
      totalStockQuantity,
      productsWithVariants,
      featuredProducts,
    };
  }

  /**
   * Process categories data
   */
  private static processCategoriesData(categoriesSnapshot: any) {
    let totalCategories = 0;
    let activeCategories = 0;

    categoriesSnapshot.forEach((doc: any) => {
      const category = doc.data();
      totalCategories++;
      if (category.isActive) {
        activeCategories++;
      }
    });

    return {
      totalCategories,
      activeCategories,
    };
  }

  /**
   * Process brands data
   */
  private static processBrandsData(brandsSnapshot: any) {
    let totalBrands = 0;
    let activeBrands = 0;

    brandsSnapshot.forEach((doc: any) => {
      const brand = doc.data();
      totalBrands++;
      if (brand.isActive) {
        activeBrands++;
      }
    });

    return {
      totalBrands,
      activeBrands,
    };
  }

  /**
   * Process warehouses data
   */
  private static async processWarehousesData(warehouses: any[]) {
    let totalWarehouses = 0;
    let activeWarehouses = 0;
    const warehouseInfos: WarehouseInfo[] = [];

    for (const warehouse of warehouses) {
      totalWarehouses++;

      if (warehouse.isActive) {
        activeWarehouses++;
      }

      const total = warehouse.capacity?.total || 0;
      const used = warehouse.capacity?.used || 0;
      const available = warehouse.capacity?.available || (total - used);
      const utilizationPercentage = total > 0 ? Math.round((used / total) * 100) : 0;

      // Get warehouse stock summary
      let productCount = 0;
      let totalStock = 0;
      
      try {
        const stockSummary = await WarehouseInventoryService.getWarehouseStockSummary(warehouse.id!);
        productCount = stockSummary.totalProducts;
        totalStock = stockSummary.totalStock;
      } catch (error) {
        console.error(`Error getting stock summary for warehouse ${warehouse.id}:`, error);
        productCount = warehouse.stats?.productCount || 0;
        totalStock = warehouse.stats?.totalStock || 0;
      }

      warehouseInfos.push({
        id: warehouse.id!,
        name: warehouse.name || 'Unknown',
        code: warehouse.code || 'N/A',
        isActive: warehouse.isActive || false,
        total,
        used,
        available,
        productCount,
        totalStock,
        utilizationPercentage,
      });
    }

    return {
      totalWarehouses,
      activeWarehouses,
      warehouses: warehouseInfos,
    };
  }

  /**
   * Process orders data
   */
  private static processOrdersData(ordersSnapshot: any) {
    let totalOrders = 0;
    let pendingOrders = 0;
    let confirmedOrders = 0;
    let completedOrders = 0;
    let cancelledOrders = 0;
    let totalRevenue = 0;

    const revenueByStatus: RevenueByStatus = {
      completed: 0,
      pending: 0,
      confirmed: 0,
      cancelled: 0,
      delivered: 0,
    };

    // Sort orders by date for recent orders
    const ordersArray = ordersSnapshot.docs.map((doc: any) => ({
      id: doc.id,
      ...doc.data(),
    }));

    ordersArray.forEach((order: any) => {
      totalOrders++;

      const status = (order.status || 'unknown').toLowerCase();
      const amount = order.totalAmount || 0;

      // Count orders by status and calculate revenue
      switch (status) {
        case 'pending':
          pendingOrders++;
          revenueByStatus.pending += amount;
          break;
        case 'confirmed':
          confirmedOrders++;
          revenueByStatus.confirmed += amount;
          break;
        case 'completed':
          completedOrders++;
          revenueByStatus.completed += amount;
          break;
        case 'delivered':
          completedOrders++; // Count delivered as completed
          revenueByStatus.delivered += amount;
          break;
        case 'cancelled':
          cancelledOrders++;
          revenueByStatus.cancelled += amount;
          break;
      }

      // Calculate total revenue (all non-cancelled orders)
      if (status !== 'cancelled') {
        totalRevenue += amount;
      }
    });

    // Get recent orders (top 10)
    const recentOrders: OrderInfo[] = ordersArray
      .sort((a: any, b: any) => {
        const aDate = a.orderDate?.toDate?.() || a.orderDate || new Date(0);
        const bDate = b.orderDate?.toDate?.() || b.orderDate || new Date(0);
        return bDate - aDate;
      })
      .slice(0, 10)
      .map((order: any) => {
        // Handle orderDate conversion safely
        let orderDate = new Date();
        if (order.orderDate) {
          if (typeof order.orderDate.toDate === 'function') {
            orderDate = order.orderDate.toDate();
          } else if (order.orderDate instanceof Date) {
            orderDate = order.orderDate;
          } else if (typeof order.orderDate === 'string' || typeof order.orderDate === 'number') {
            orderDate = new Date(order.orderDate);
          }
        }

        return {
          id: order.id,
          orderNumber: order.orderNumber || 'N/A',
          customerName: order.customerName || 'Unknown',
          customerEmail: order.customerEmail || '',
          itemCount: order.items?.length || 0,
          totalAmount: order.totalAmount || 0,
          status: order.status || 'unknown',
          paymentStatus: order.paymentStatus || 'unknown',
          orderDate: orderDate,
        };
      });

    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    return {
      totalOrders,
      pendingOrders,
      confirmedOrders,
      completedOrders,
      cancelledOrders,
      totalRevenue,
      averageOrderValue,
      recentOrders,
      revenueByStatus,
    };
  }

  /**
   * Get product distribution by category
   */
  private static getProductsByCategoryDistribution(
    productsSnapshot: any,
    categoriesSnapshot: any
  ): CategoryDistribution[] {
    // Create category map
    const categoryMap = new Map<string, string>();
    categoriesSnapshot.forEach((doc: any) => {
      categoryMap.set(doc.id, doc.data().name || 'Unknown');
    });

    // Count products per category
    const categoryCounts = new Map<string, number>();
    let totalProducts = 0;

    productsSnapshot.forEach((doc: any) => {
      const product = doc.data();
      const categoryId = product.categoryId || 'uncategorized';
      categoryCounts.set(categoryId, (categoryCounts.get(categoryId) || 0) + 1);
      totalProducts++;
    });

    // Convert to array and calculate percentages
    const distribution: CategoryDistribution[] = [];
    categoryCounts.forEach((count, categoryId) => {
      distribution.push({
        categoryId,
        categoryName: categoryMap.get(categoryId) || 'Uncategorized',
        productCount: count,
        percentage: totalProducts > 0 ? Math.round((count / totalProducts) * 100) : 0,
      });
    });

    // Sort by product count descending
    return distribution.sort((a, b) => b.productCount - a.productCount);
  }

  /**
   * Get stock alerts from warehouse inventory (similar to report service)
   */
  private static async getStockAlertsFromInventory(
    productsSnapshot: any,
    allInventory: InventoryItem[],
    productInventorySummary: Map<string, ProductInventorySummary>
  ): Promise<StockAlert[]> {
    const alerts: StockAlert[] = [];
    const alertMap = new Map<string, StockAlert>(); // To avoid duplicates

    // Create product map for quick lookup
    const productMap = new Map<string, any>();
    productsSnapshot.forEach((doc: any) => {
      productMap.set(doc.id, { id: doc.id, ...doc.data() });
    });

    // Group inventory by product/variant
    const inventoryByProduct: Map<string, InventoryItem[]> = new Map();
    allInventory.forEach((item: InventoryItem) => {
      const key = item.variantId ? `${item.productId}-${item.variantId}` : item.productId;
      if (!inventoryByProduct.has(key)) {
        inventoryByProduct.set(key, []);
      }
      inventoryByProduct.get(key)!.push(item);
    });

    // Check each product/variant
    inventoryByProduct.forEach((inventoryItems, key) => {
      const [productId, variantId] = key.includes('-') ? key.split('-') : [key, undefined];
      const product = productMap.get(productId);
      
      if (!product) return;

      let totalStock = 0;
      let minStockLevel = 0;
      let isOutOfStock = true;
      let isLowStock = false;
      const warehouseCount = inventoryItems.length;

      inventoryItems.forEach((item: InventoryItem) => {
        totalStock += item.quantity;
        
        // Use the highest min stock level across warehouses
        if (item.minStockLevel > minStockLevel) {
          minStockLevel = item.minStockLevel;
        }
        
        // Item is out of stock if quantity is 0
        if (item.quantity > 0) {
          isOutOfStock = false;
        }
        
        if (item.isLowStock) {
          isLowStock = true;
        }
      });

      // Determine alert type
      const alertType = totalStock === 0 ? 'out' : (isLowStock ? 'low' : null);
      
      if (!alertType) return; // No alert needed

      if (variantId) {
        // Variant product
        const variant = product.variants?.find((v: any) => v.id === variantId);
        if (variant) {
          const variantName = variant.variantSelections
            ?.map((s: any) => s.variantValue)
            .join(' - ') || 'Unknown Variant';
          
          const alertKey = `${productId}-${variantId}`;
          if (!alertMap.has(alertKey)) {
            alertMap.set(alertKey, {
              productId,
              productName: product.name || 'Unknown',
              sku: variant.sku || 'N/A',
              stockQuantity: totalStock,
              minStockLevel: minStockLevel || variant.minStockLevel || product.minStockLevel || 0,
              alertType,
              warehouseCount,
              variantId,
              variantName,
            });
          }
        }
      } else {
        // Simple product
        const alertKey = `${productId}`;
        if (!alertMap.has(alertKey)) {
          alertMap.set(alertKey, {
            productId,
            productName: product.name || 'Unknown',
            sku: product.sku || 'N/A',
            stockQuantity: totalStock,
            minStockLevel: minStockLevel || product.minStockLevel || 0,
            alertType,
            warehouseCount,
          });
        }
      }
    });

    // Check for products/variants that have no inventory records
    productMap.forEach((product, productId) => {
      const inventorySummary = productInventorySummary.get(productId);
      
      if (!inventorySummary) return;

      if (product.hasVariants) {
        // For products with variants, check each variant
        const variants = product.variants || [];
        variants.forEach((variant: any) => {
          const variantKey = `${productId}-${variant.id}`;
          
          // Only add alert if variant has no inventory records
          if (!alertMap.has(variantKey)) {
            const variantInventory = inventorySummary.variants?.[variant.id];
            
            // Variant is out of stock if it has no inventory or all inventory is 0
            if (!variantInventory || variantInventory.isOutOfStock) {
              const variantName = variant.variantSelections
                ?.map((s: any) => s.variantValue)
                .join(' - ') || 'Unknown Variant';
              
              alertMap.set(variantKey, {
                productId,
                productName: product.name || 'Unknown',
                sku: variant.sku || 'N/A',
                stockQuantity: 0,
                minStockLevel: variant.minStockLevel || product.minStockLevel || 0,
                alertType: 'out',
                warehouseCount: 0,
                variantId: variant.id,
                variantName,
              });
            }
          }
        });
      } else {
        // Simple product
        if (!alertMap.has(productId)) {
          // Product is out of stock if it has no inventory or all inventory is 0
          if (inventorySummary.isOutOfStock) {
            alertMap.set(productId, {
              productId,
              productName: product.name || 'Unknown',
              sku: product.sku || 'N/A',
              stockQuantity: 0,
              minStockLevel: product.minStockLevel || 0,
              alertType: 'out',
              warehouseCount: 0,
            });
          }
        }
      }
    });

    // Convert map to array
    alertMap.forEach((alert) => {
      alerts.push(alert);
    });

    // Sort by alert type (out of stock first) and then by stock quantity
    alerts.sort((a, b) => {
      if (a.alertType === 'out' && b.alertType === 'low') return -1;
      if (a.alertType === 'low' && b.alertType === 'out') return 1;
      return a.stockQuantity - b.stockQuantity;
    });

    return alerts.slice(0, 10); // Return top 10 alerts
  }

  /**
   * Get order trends for the last 7 days
   */
  private static getOrderTrends(ordersSnapshot: any): OrderTrend[] {
    try {
      // Group by date
      const trendMap = new Map<string, { orders: number; revenue: number }>();
      
      // Get current date in local timezone
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6); // Include today, so -6 days

      ordersSnapshot.forEach((doc: any) => {
        const order = doc.data();
        
        // Handle orderDate conversion
        let orderDate: Date | null = null;
        const dateField = order.orderDate || order.createdAt || order.updatedAt;
        
        if (dateField) {
          if (typeof dateField.toDate === 'function') {
            try {
              orderDate = dateField.toDate();
            } catch (e) {
              console.warn('toDate() failed for order:', doc.id);
            }
          } else if (dateField.seconds !== undefined && dateField.seconds !== null) {
            orderDate = new Date(dateField.seconds * 1000);
          } else if (dateField instanceof Date) {
            orderDate = dateField;
          } else if (typeof dateField === 'string' || typeof dateField === 'number') {
            const parsedDate = new Date(dateField);
            if (!isNaN(parsedDate.getTime())) {
              orderDate = parsedDate;
            }
          }
        }
        
        if (!orderDate || isNaN(orderDate.getTime())) {
          return;
        }

        // Normalize to date only (remove time component) in local timezone
        const orderDateOnly = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());

        // Check if order is within last 7 days (including today)
        if (orderDateOnly < sevenDaysAgo || orderDateOnly > today) {
          return;
        }

        // Create date key in local timezone
        const year = orderDateOnly.getFullYear();
        const month = String(orderDateOnly.getMonth() + 1).padStart(2, '0');
        const day = String(orderDateOnly.getDate()).padStart(2, '0');
        const dateKey = `${year}-${month}-${day}`;
        
        const existing = trendMap.get(dateKey) || { orders: 0, revenue: 0 };

        trendMap.set(dateKey, {
          orders: existing.orders + 1,
          revenue: existing.revenue + (order.totalAmount || 0),
        });
      });

      // Create array for all 7 days (including today) in local timezone
      const trends: OrderTrend[] = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateKey = `${year}-${month}-${day}`;
        
        const data = trendMap.get(dateKey) || { orders: 0, revenue: 0 };

        trends.push({
          date: dateKey,
          orders: data.orders,
          revenue: data.revenue,
        });
      }

      return trends;
    } catch (error) {
      console.error('Error in getOrderTrends:', error);
      // Return empty 7-day array on error
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const trends: OrderTrend[] = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateKey = `${year}-${month}-${day}`;
        
        trends.push({
          date: dateKey,
          orders: 0,
          revenue: 0,
        });
      }
      return trends;
    }
  }

  /**
   * Get top selling products
   */
  private static getTopProducts(ordersSnapshot: any): TopProduct[] {
    const productSales = new Map<string, { name: string; sold: number; revenue: number; image?: string }>();

    ordersSnapshot.forEach((doc: any) => {
      const order = doc.data();
      const items = order.items || [];

      items.forEach((item: any) => {
        const productId = item.productId;
        if (!productId) return;

        const existing = productSales.get(productId) || {
          name: item.productName || 'Unknown',
          sold: 0,
          revenue: 0,
          image: item.productImage,
        };

        productSales.set(productId, {
          name: existing.name,
          sold: existing.sold + (item.quantity || 0),
          revenue: existing.revenue + (item.totalPrice || 0),
          image: existing.image || item.productImage,
        });
      });
    });

    // Convert to array and sort by quantity sold
    const topProducts: TopProduct[] = [];
    productSales.forEach((data, productId) => {
      topProducts.push({
        productId,
        productName: data.name,
        totalSold: data.sold,
        revenue: data.revenue,
        imageUrl: data.image,
      });
    });

    return topProducts.sort((a, b) => b.totalSold - a.totalSold).slice(0, 5);
  }
}

export default DashboardService;